import { z } from 'zod';
import { NoticeCodeSchema } from './notice-codes';

// Workspace-addressed: every assistant route hangs off
// /v1/workspaces/:workspaceId/... and is owner-scoped in the handler.
const WorkspaceIdPathParamsSchema = z.object({ workspaceId: z.uuid() });
const ConversationPathParamsSchema = WorkspaceIdPathParamsSchema.extend({ conversationId: z.uuid() });

export const CONVERSATION_TITLE_MAX_LENGTH = 120;
export const MESSAGE_CONTENT_MAX_LENGTH = 10_000;
export const PAGE_TEXT_MAX_LENGTH = 20_000;
export const PAGE_TITLE_MAX_LENGTH = 300;
export const PAGE_URL_MAX_LENGTH = 2_048;

// The universe of platform model ids the assistant knows. This is the wire
// enum: a posted `model` must be one of these, so an unknown id is rejected at
// schema validation (400) rather than reaching the engine. Which of these are
// actually *offered* is a narrower, config-driven + servable subset the backend
// resolves (see the model registry) and exposes via the discovery endpoint.
export const ASSISTANT_MODEL_IDS = [
  'nvidia.nemotron-nano-3-30b',
  'nvidia.nemotron-super-3-120b',
  'openai.gpt-oss-120b',
  'amazon.nova-pro-v1:0',
  // Anthropic ids stay known but are on hold (not in the default allowlist).
  'anthropic.claude-sonnet-5',
  'anthropic.claude-opus-4-8',
  'anthropic.claude-haiku-4-5',
] as const;
export const AssistantModelIdSchema = z.enum(ASSISTANT_MODEL_IDS);
export type AssistantModelId = z.infer<typeof AssistantModelIdSchema>;

// Only USER messages are written this slice; ASSISTANT / TOOL arrive with the
// run engine (IMM-307.2). The role enum is fixed now so the message shape is
// stable across slices.
export const MESSAGE_ROLES = ['USER', 'ASSISTANT', 'TOOL'] as const;
export const MessageRoleSchema = z.enum(MESSAGE_ROLES);
export type MessageRole = z.infer<typeof MessageRoleSchema>;

// 'MESSAGE' is an ordinary conversation turn; 'NOTICE' is an engine-emitted
// stop/fail surface that carries a `noticeCode` for the client to localize
// (IMM-307.3).
// 'PAGE_REQUEST' — the run asked the member's browser for the open page (role TOOL, empty content).
// 'PAGE_READ' — the page the browser handed over (role TOOL, content = visible text, `page` = title + address).
export const MESSAGE_KINDS = ['MESSAGE', 'NOTICE', 'PAGE_REQUEST', 'PAGE_READ'] as const;
export const MessageKindSchema = z.enum(MESSAGE_KINDS);
export type MessageKind = z.infer<typeof MessageKindSchema>;

export const ConversationSchema = z.object({
  id: z.uuid(),
  title: z.string().nullable(),
  createdAt: z.iso.datetime(),
  lastActivityAt: z.iso.datetime(),
});
export type Conversation = z.infer<typeof ConversationSchema>;

export const PageReferenceSchema = z.object({ title: z.string(), url: z.string() });
export type PageReference = z.infer<typeof PageReferenceSchema>;

export const MessageSchema = z.object({
  id: z.uuid(),
  conversationId: z.uuid(),
  role: MessageRoleSchema,
  // `kind` distinguishes ordinary turns from engine notices; `noticeCode` is the
  // i18n key set when kind='NOTICE', null otherwise (IMM-307.3).
  kind: MessageKindSchema,
  noticeCode: NoticeCodeSchema.nullable(),
  // The page a PAGE_READ holds; set iff kind = 'PAGE_READ'.
  page: PageReferenceSchema.nullable(),
  content: z.string(),
  sequence: z.number().int().nonnegative(),
  // The platform model that produced this turn (IMM-348): the chosen model on a
  // USER message, the run's model on an ASSISTANT/TOOL turn. Null for messages
  // predating per-message model tracking. Clients seed the composer from the
  // latest non-null value when reopening a conversation.
  model: AssistantModelIdSchema.nullable(),
  createdAt: z.iso.datetime(),
});
export type Message = z.infer<typeof MessageSchema>;

export const CreateConversationPathParamsSchema = WorkspaceIdPathParamsSchema;
export const CreateConversationBodySchema = z
  .object({
    title: z.string().trim().min(1).max(CONVERSATION_TITLE_MAX_LENGTH).optional(),
  })
  .strict();
export type CreateConversationBody = z.infer<typeof CreateConversationBodySchema>;
export const CreateConversationResponseSchema = ConversationSchema;

export const ListConversationsPathParamsSchema = WorkspaceIdPathParamsSchema;
export const ListConversationsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional(),
  // Opaque keyset cursor from a previous page's `nextCursor`.
  cursor: z.string().optional(),
});
export const ListConversationsResponseSchema = z.object({
  conversations: z.array(ConversationSchema),
  // Pass back as `?cursor=` for the next page; null when the list is exhausted.
  nextCursor: z.string().nullable(),
});

export const GetConversationPathParamsSchema = ConversationPathParamsSchema;
// Unlike the list item, the single-conversation read carries `tokensUsed` — the
// summed token usage across the conversation's runs (IMM-348) — so the debug
// token counter can seed its running total. Kept off the list schema to avoid a
// per-row aggregate on the conversation list.
export const GetConversationResponseSchema = ConversationSchema.extend({
  tokensUsed: z.number().int().nonnegative(),
});
export type GetConversationResponse = z.infer<typeof GetConversationResponseSchema>;

export const PostMessagePathParamsSchema = ConversationPathParamsSchema;
export const PostMessageBodySchema = z
  .object({
    content: z.string().trim().min(1).max(MESSAGE_CONTENT_MAX_LENGTH),
    // Optional platform model id for this message's run (IMM-351.1). Omitted →
    // the backend's default model; a value must be one of the known model ids
    // (unknown → 400 here). A known-but-not-offered id is further rejected by the
    // backend allowlist. Clients that don't switch omit the field entirely.
    model: AssistantModelIdSchema.optional(),
  })
  .strict();
export type PostMessageBody = z.infer<typeof PostMessageBodySchema>;
export const PostMessageResponseSchema = MessageSchema;

// The selectable model set the composer discovers (IMM-351.1). `id` is the
// platform model id posted back on a message and the client's i18n key for the
// display label (backend defines the identity, i18n translates it — IMM-348).
// Exactly one entry is flagged `isDefault`.
export const AssistantModelSchema = z.object({
  id: AssistantModelIdSchema,
  isDefault: z.boolean(),
});
export type AssistantModel = z.infer<typeof AssistantModelSchema>;

export const ListModelsPathParamsSchema = WorkspaceIdPathParamsSchema;
export const ListModelsResponseSchema = z.object({
  models: z.array(AssistantModelSchema),
});

export const RecordPageReadPathParamsSchema = ConversationPathParamsSchema;
export const RecordPageReadBodySchema = z.discriminatedUnion('outcome', [
  z
    .object({
      requestId: z.uuid(),
      outcome: z.literal('READ'),
      page: z
        .object({
          url: z.url().max(PAGE_URL_MAX_LENGTH),
          title: z.string().trim().min(1).max(PAGE_TITLE_MAX_LENGTH),
          text: z.string().max(PAGE_TEXT_MAX_LENGTH),
        })
        .strict(),
    })
    .strict(),
  z.object({ requestId: z.uuid(), outcome: z.literal('NOT_SUPPORTED') }).strict(),
]);
export type RecordPageReadBody = z.infer<typeof RecordPageReadBodySchema>;

export const ListMessagesPathParamsSchema = ConversationPathParamsSchema;
export const ListMessagesQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional(),
  // Opaque keyset cursor from a previous page's `nextCursor`.
  cursor: z.string().optional(),
});
export const ListMessagesResponseSchema = z.object({
  messages: z.array(MessageSchema),
  // Pass back as `?cursor=` for the next page; null when the history is exhausted.
  nextCursor: z.string().nullable(),
});

// Path params for the SSE run-progress stream (IMM-307.4). Re-exported so the
// stream contract entry can address one conversation by id.
export { ConversationPathParamsSchema };

// SSE event payload for the live run-progress stream (IMM-307.4). Mirrors the
// backend `RunOutputEvent` union: a `step` indication, a `token` delta
// (IMM-348.1), and a terminal event. Carried as the `event` schema on the stream
// contract leaf so the contract is meaningful for docs/clients — the persisted
// message log stays authoritative.
export const RunStreamEventSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('step'),
    conversationId: z.uuid(),
    runId: z.uuid(),
    step: z.string(),
  }),
  z.object({
    type: z.literal('token'),
    conversationId: z.uuid(),
    runId: z.uuid(),
    // A chunk of the assistant's answer as the model produces it (IMM-348.1).
    // Purely a live-rendering aid; the persisted final message stays the source
    // of truth, so a dropped or out-of-order token never corrupts the log.
    text: z.string(),
  }),
  z.object({
    type: z.literal('usage'),
    conversationId: z.uuid(),
    runId: z.uuid(),
    // Cumulative token usage for THIS run so far (IMM-348), emitted after each
    // model call. A live-rendering aid for the debug token counter; the run's
    // persisted usage stays authoritative.
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
  }),
  z.object({
    type: z.enum(['completed', 'stopped', 'failed']),
    conversationId: z.uuid(),
    runId: z.uuid(),
    messageId: z.uuid(),
    // The i18n notice key for a stop/fail surface; falls back to a free string
    // so an unknown engine code never breaks the wire contract.
    noticeCode: z.union([NoticeCodeSchema, z.string()]).optional(),
  }),
  // The run asked the member's browser for the open page and ended (IMM-1393.3).
  z.object({
    type: z.literal('page-requested'),
    conversationId: z.uuid(),
    runId: z.uuid(),
    messageId: z.uuid(),
  }),
]);
export type RunStreamEvent = z.infer<typeof RunStreamEventSchema>;
